'use client'

import React, { useState, useMemo } from 'react'
import { Typography } from 'components/Contentful/Typography/Typography'
import { MediaImageStandard } from 'components/Contentful/MediaImageStandard/MediaImageStandard'
import { AchieveLink } from 'components/AchieveLink'
import { StarRating } from 'components/StarRating'
import { Check, ThumbsUp, ThumbsDown, X, Settings, Info } from 'react-feather'
import { Tabs, Tab } from '@achieve/ascend'
import Link from 'next/link'
import { Button } from '@achieve/ascend'
import BillsRatingModal from '../BillsRatingModal/BillsRatingModal'
import { withUuid } from 'utils/shared'

export const ServiceItem = ({ value }) => {
  if (!value || !value.length) {
    return null
  }

  const symbolMatch = value.match(/^[✓,✖]/)
  const symbolText = symbolMatch ? symbolMatch[0] : '✓'
  const valueText = value.replace(/^[✓,✖]/, '').trim()

  const icons = {
    '✓': <Check className="w-4 min-w-4 h-4 min-h-4 text-green-500 font-bold" strokeWidth={4} />,
    '✖': <X className="w-4 min-w-4 h-4 min-h-4 text-red-500 font-bold" strokeWidth={4} />,
  }

  return (
    <li className="list-none p-0 flex gap-2">
      {icons[symbolText]} <Typography content={valueText} className="bodyMd" />
    </li>
  )
}

export const ProviderReview = ({
  providerType,
  providerData,
  logoUrl,
  providerWebsiteLink,
  rating,
  bestTitle,
  description,
}) => {
  const [tabValue, setTabValue] = useState(providerData?.table?.length > 0 ? 0 : null)
  const [modalStatus, setModalStatus] = useState(false)

  const handleTabChange = (_localEvent, newValue) => {
    setTabValue(newValue)
  }

  const services = useMemo(
    () =>
      typeof providerData?.services === 'string'
        ? providerData?.services?.split('\n')
        : providerData?.services ?? [],
    [providerData?.services]
  )

  const pros = useMemo(
    () =>
      typeof providerData?.pros === 'string'
        ? providerData?.pros?.split('\n')
        : providerData?.pros?.content?.map((item) => item.content[0].value) ?? [],
    [providerData?.pros]
  )

  const cons = useMemo(
    () =>
      typeof providerData?.cons === 'string'
        ? providerData?.cons?.split('\n')
        : providerData?.cons?.content?.map((item) => item.content[0].value) ?? [],
    [providerData?.cons]
  )

  const keyFeatures = useMemo(
    () =>
      typeof providerData?.keyFeatures === 'string'
        ? providerData?.keyFeatures?.split('\n')
        : providerData?.keyFeatures ?? [],
    [providerData?.keyFeatures]
  )

  const servicesList = useMemo(() => withUuid(services), [services])
  const prosList = useMemo(() => withUuid(pros), [pros])
  const consList = useMemo(() => withUuid(cons), [cons])
  const keyFeaturesList = useMemo(() => withUuid(keyFeatures), [keyFeatures])

  return (
    <div className="flex flex-col gap-4 my-4">
      {bestTitle && <Typography content={bestTitle} className="headingLg" />}
      {(bestTitle || logoUrl || rating || providerWebsiteLink) && (
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-8 items-start lg:items-center">
          {logoUrl && (
            <div className="w-[200px] h-full relative">
              <MediaImageStandard
                content={logoUrl}
                layout="responsive"
                className="w-[200px] h-auto"
              />
            </div>
          )}
          {rating && (
            <div className="flex flex-col gap-2">
              <Typography content={'Overall rating:'} className="headingMd" />
              <div className="flex gap-2 items-center">
                <Typography content={`${rating}`} className="headingLg" />
                <StarRating rating={Number(rating)} size={24} />
                <div
                  className="flex items-center"
                  onClick={() => {
                    setModalStatus(true)
                  }}
                >
                  <Typography
                    content={'Bills.com rating'}
                    className="bodyXs cursor-pointer text-[#22408c]"
                  />
                  <Info className="w-3 h-3 text-[#22408c] ml-1" strokeWidth={2} />
                </div>
              </div>
            </div>
          )}
          {providerWebsiteLink && (
            <div className="flex flex-col justify-end h-auto lg:h-[70px] mb-4">
              <AchieveLink href={providerWebsiteLink} target="_blank">
                <Typography
                  content={"Go to the provider's site"}
                  className="bodyXs cursor-pointer font-bold! underline"
                />
              </AchieveLink>
            </div>
          )}
        </div>
      )}
      {description && <Typography content={description} className="bodyMd" />}
      <div className="flex gap-2 flex-col lg:flex-row">
        <div className="w-full flex-1">
          {providerType !== 'homeEquityProvider' ? (
            <table className="w-full border-collapse">
              <tbody>
                {/* Counseling Services */}
                {services && services.length > 0 && (
                  <tr className="odd:bg-gray-100 even:bg-white">
                    <td className="p-2 flex">
                      <Typography content={'Counseling Services'} className="displayXs" />
                    </td>
                    <td className="p-2">
                      <ul className="list-none p-0">
                        {servicesList.map((item) => (
                          <ServiceItem value={item.value} key={item.uuid} />
                        ))}
                      </ul>
                    </td>
                  </tr>
                )}
                {/* Type of Debt Settled */}
                {providerData?.debtSettled && (
                  <tr className="odd:bg-gray-100 even:bg-white">
                    <td className="p-2 flex">
                      <Typography content={'Type of Debt Settled'} className="displayXs" />
                    </td>
                    <td className="p-2">
                      <Typography content={providerData?.debtSettled} className="bodyMd" />
                    </td>
                  </tr>
                )}
                {/* Program Length */}
                {providerData?.programLength && (
                  <tr className="odd:bg-gray-100 even:bg-white">
                    <td className="p-2 flex">
                      <Typography content={'Program Length'} className="displayXs" />
                    </td>
                    <td className="p-2">
                      <Typography content={providerData?.programLength} className="bodyMd" />
                    </td>
                  </tr>
                )}
                {/* Customer Service Hours */}
                {providerData?.programLength && (
                  <tr className="odd:bg-gray-100 even:bg-white">
                    <td className="p-2 flex">
                      <Typography content={'Customer Service Hours'} className="displayXs" />
                    </td>
                    <td className="p-2">
                      <Typography content={providerData?.customerSupport} className="bodyMd" />
                    </td>
                  </tr>
                )}
                {/* Coverage */}
                {providerData?.coverage && (
                  <tr className="odd:bg-gray-100 even:bg-white">
                    <td className="p-2 flex">
                      <Typography content={'Coverage'} className="displayXs" />
                    </td>
                    <td className="p-2">
                      <Typography content={providerData?.coverage} className="bodyMd" />
                      <Typography
                        content={providerData?.coverageNotes}
                        className="bodyMd text-gray-500"
                      />
                    </td>
                  </tr>
                )}
                {/* Accreditation */}
                {providerData?.accreditation && (
                  <tr className="odd:bg-gray-100 even:bg-white">
                    <td className="p-2 flex">
                      <Typography content={'Accreditation'} className="displayXs" />
                    </td>
                    <td className="p-2">
                      <Typography content={providerData?.accreditation} className="bodyMd" />
                    </td>
                  </tr>
                )}
                {/* Fixed Rate */}
                {providerData?.fixedApr && (
                  <tr className="odd:bg-gray-100 even:bg-white">
                    <td className="p-2 flex">
                      <Typography content={'Fixed Rate'} className="displayXs" />
                    </td>
                    <td className="p-2">
                      <Typography content={providerData?.fixedApr} className="bodyMd" />
                    </td>
                  </tr>
                )}
                {/* Loan Amount */}
                {providerData?.loanAmounts && (
                  <tr className="odd:bg-gray-100 even:bg-white">
                    <td className="p-2 flex">
                      <Typography content={'Loan Amount'} className="displayXs" />
                    </td>
                    <td className="p-2">
                      <Typography content={providerData?.loanAmounts} className="bodyMd" />
                    </td>
                  </tr>
                )}
                {/* Repayment */}
                {providerData?.repayment && (
                  <tr className="odd:bg-gray-100 even:bg-white">
                    <td className="p-2 flex">
                      <Typography content={'Repayment'} className="displayXs" />
                    </td>
                    <td className="p-2">
                      <Typography content={providerData?.repayment} className="bodyMd" />
                    </td>
                  </tr>
                )}
                {/* Fees */}
                {providerData?.fees && (
                  <tr className="odd:bg-gray-100 even:bg-white">
                    <td className="p-2 flex">
                      <Typography content={'Fees'} className="displayXs" />
                    </td>
                    <td className="p-2">
                      <Typography content={providerData?.fees} className="bodyMd" />
                    </td>
                  </tr>
                )}
                {/* Time to Found */}
                {providerData?.timeToFund && (
                  <tr className="odd:bg-gray-100 even:bg-white">
                    <td className="p-2 flex">
                      <Typography content={'Time to Found'} className="displayXs" />
                    </td>
                    <td className="p-2">
                      <Typography content={providerData?.timeToFund} className="bodyMd" />
                    </td>
                  </tr>
                )}
                {/* Min. Credit Score */}
                {providerData?.minCreditScore && (
                  <tr className="odd:bg-gray-100 even:bg-white">
                    <td className="p-2 flex">
                      <Typography content={'Min. Credit Score'} className="displayXs" />
                    </td>
                    <td className="p-2">
                      <Typography content={providerData?.minCreditScore} className="bodyMd" />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-2">
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile={true}
                  aria-label="sticky scrollable tabs component"
                >
                  {providerData?.table.map(({ fields }, i) => (
                    <Tab
                      key={`tab-${i}`}
                      label={fields?.label}
                      value={i}
                      data-testid={`tab-${i}`}
                    />
                  ))}
                </Tabs>
              </div>
              {providerData?.table?.[tabValue] && (
                <table className="w-full border-collapse">
                  <tbody>
                    {/* Amount */}
                    <tr className="odd:bg-gray-100 even:bg-white">
                      <td className="p-2 flex">
                        <Typography content={'Amount'} className="displayXs" />
                      </td>
                      <td className="p-2">
                        <Typography
                          content={providerData?.table?.[tabValue]?.fields?.amount}
                          className="bodyMd"
                        />
                      </td>
                    </tr>
                    {/* Terms */}
                    <tr className="odd:bg-gray-100 even:bg-white">
                      <td className="p-2 flex">
                        <Typography content={'Terms'} className="displayXs" />
                      </td>
                      <td className="p-2">
                        <Typography
                          content={providerData?.table?.[tabValue]?.fields?.term}
                          className="bodyMd"
                        />
                      </td>
                    </tr>
                    {/* Rates */}
                    <tr className="odd:bg-gray-100 even:bg-white">
                      <td className="p-2 flex">
                        <Typography content={'Rates'} className="displayXs" />
                      </td>
                      <td className="p-2">
                        <Typography
                          content={providerData?.table?.[tabValue]?.fields?.rates}
                          className="bodyMd"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
        <table className="w-full border-collapse flex-1">
          <tbody>
            {/* Pros and Cons */}
            {pros.length > 0 && cons.length > 0 && (
              <tr className="odd:bg-gray-100 even:bg-white flex gap-2 p-4">
                <td className="flex flex-col flex-1 gap-2">
                  <ThumbsUp className="w-8 h-8 text-green-600" strokeWidth={2} />
                  <Typography content={'Pros:'} className="displayXs text-green-600" />
                  <ul className="flex gap-2 flex-col pl-4">
                    {prosList.map((item) => (
                      <li key={item.uuid}>{item.value}</li>
                    ))}
                  </ul>
                </td>
                <td className="flex flex-col flex-1 gap-2">
                  <ThumbsDown className="w-8 h-8 text-red-600" strokeWidth={2} />
                  <Typography content={'Cons:'} className="displayXs text-red-600" />
                  <ul className="flex gap-2 flex-col pl-4">
                    {consList.map((item) => (
                      <li key={item.uuid}>{item.value}</li>
                    ))}
                  </ul>
                </td>
              </tr>
            )}
            {/* Key Features */}
            {keyFeatures && keyFeatures.length > 0 && (
              <tr className="odd:bg-gray-100 even:bg-white" col>
                <td className="p-4 flex flex-col gap-2">
                  <div className="flex gap-2 items-center">
                    <Settings className="w-8 h-8 text-[#22408c]" strokeWidth={2} />{' '}
                    <Typography content={'Key Features'} className="displayXs" />
                  </div>
                  <ul className="list-none p-0 flex flex-col gap-4">
                    {keyFeaturesList.map((item) => (
                      <ServiceItem value={item.value} key={item.uuid} />
                    ))}
                  </ul>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex flex-col gap-2 my-4">
        <Link href={`#article-compare-section`} className="no-underline">
          <Button variant="outlined" className="w-full">
            <Typography content="Click here" className="displaySm" />
          </Button>
        </Link>
      </div>
      <BillsRatingModal
        modalStatus={modalStatus}
        setModalStatus={setModalStatus}
        content="Bills.com ratings are determined by the editorial team. Lenders overall score is calculated based on rates, fees, customer service availability, and other factors. Our editors' assessment helps you make your own decisions on the best provider for your needs."
      />
    </div>
  )
}

export default ProviderReview
